/*
	BlurText component from React Bits
	Based on https://www.reactbits.dev/text-animations/blur-text
*/

import { useRef, useEffect, useState } from "react";

const BlurText = ({
  text = "",
  animateBy = "words", // "words" or "letters"
  direction = "top", // "top" or "bottom"
  delay = 10, // Delay between animations for each word/letter (in ms)
  stepDuration = 0.35, // The time taken for each letter/word to animate (in seconds)
  threshold = 0.1, // Intersection threshold for triggering the animation
  rootMargin = "0px", // Root margin for the intersection observer
  onAnimationComplete, // Callback function triggered when all animations complete
  className = "",
  ...props
}) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedItems, setAnimatedItems] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          startAnimation();
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [threshold, rootMargin, isVisible]);

  const startAnimation = () => {
    const items = animateBy === "words" ? text.split(" ") : text.split("");
    
    items.forEach((item, index) => {
      setTimeout(() => {
        setAnimatedItems(prev => [...prev, index]);
        
        // Check if this is the last item
        if (index === items.length - 1 && onAnimationComplete) {
          setTimeout(() => {
            onAnimationComplete();
          }, stepDuration * 1000);
        }
      }, index * delay);
    });
  };

  const renderAnimatedText = () => {
    const items = animateBy === "words" ? text.split(" ") : text.split("");
    
    return items.map((item, index) => {
      const isAnimated = animatedItems.includes(index);
      const animationDelay = index * delay;
      
      const baseStyle = {
        display: "inline-block",
        transition: `all ${stepDuration}s ease-out`,
        transitionDelay: `${animationDelay}ms`,
      };
      
      const animatedStyle = isAnimated
        ? {
            ...baseStyle,
            opacity: 1,
            filter: "blur(0px)",
            transform: direction === "top" ? "translateY(0)" : "translateY(0)",
          }
        : {
            ...baseStyle,
            opacity: 0,
            filter: "blur(8px)",
            transform: direction === "top" ? "translateY(-20px)" : "translateY(20px)",
          };

      if (animateBy === "words") {
        return (
          <span key={index} style={animatedStyle}>
            {item}
            {index < items.length - 1 && " "}
          </span>
        );
      } else {
        return (
          <span key={index} style={animatedStyle}>
            {item === " " ? "\u00A0" : item}
          </span>
        );
      }
    });
  };

  return (
    <div
      ref={containerRef}
      className={`blur-text-container ${className}`}
      {...props}
    >
      {renderAnimatedText()}
    </div>
  );
};

export default BlurText;